/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RouteGate
 * ---------
 * Holds back feed route downloads until someone actually stops on a card —
 * the web half of the "Feed Route Loading" proposal, matching what UpVibe's
 * Android feed does (HomeFragment + ActivityFeedViewModel + RouteDwellTracker).
 *
 * A card's route (and with it its map tiles) is requested only when:
 *   - at least VISIBLE_FRACTION of the card's map area is on screen,
 *   - the page is at rest — not scrolling, and the tab is visible,
 *   - it has stayed that way for ROUTE_DWELL_MS,
 *   - and no other route is still downloading (MAX_PARALLEL_ROUTES), topmost
 *     card first.
 *
 * Once granted, a card keeps its grant for the rest of the session: React
 * Query caches the route, so scrolling back never fetches it again. Scrolling
 * a hundred cards past without stopping costs nothing at all.
 *
 * Without a provider above them (or without IntersectionObserver — jsdom,
 * old browsers) cards fall back to loading straight away, which is the
 * behaviour the feed had before this gate existed.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  createRouteDwellTracker, ROUTE_DWELL_MS, VISIBLE_FRACTION, MAX_PARALLEL_ROUTES, SCROLL_IDLE_MS,
} from '../../pages/feed/routeDwell';

const RouteGateContext = createContext(null);

const supported = () => typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined';

/**
 * Is enough of this card's map on screen to count as "in view"? The ratio
 * alone is not enough: a card taller than the viewport can never reach half,
 * so a card filling half the viewport counts too.
 */
function enoughVisible(entry, fraction) {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= fraction) return true;
  const root = entry.rootBounds;
  return !!root && root.height > 0 && entry.intersectionRect.height >= root.height * fraction;
}

export function RouteGateProvider({
  children,
  dwellMs = ROUTE_DWELL_MS,
  visibleFraction = VISIBLE_FRACTION,
  maxParallel = MAX_PARALLEL_ROUTES,
  idleMs = SCROLL_IDLE_MS,
}) {
  const tracker = useRef(null);
  if (tracker.current === null) tracker.current = createRouteDwellTracker(dwellMs);

  // Registered cards that still want a route, their map element, whether it is
  // visible enough, and the callback that tells the card it may load.
  const cards = useRef(new Map());
  const granted = useRef(new Set());
  const inFlight = useRef(new Set());
  const moving = useRef(false);
  const idleTimer = useRef(null);
  const dwellTimer = useRef(null);
  const observer = useRef(null);
  const visibleFractionRef = useRef(visibleFraction);
  visibleFractionRef.current = visibleFraction;

  const evaluate = useCallback(() => {
    if (dwellTimer.current) { clearTimeout(dwellTimer.current); dwellTimer.current = null; }
    const now = Date.now();
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

    if (moving.current || hidden) {
      // Dragging, flinging, or nobody is looking: every dwell timer is cancelled.
      tracker.current.onListMoving();
    } else {
      // Cards in view, topmost first — the order routes are granted in.
      const waiting = [];
      for (const [id, card] of cards.current) {
        if (card.visible && card.node) waiting.push([id, card.node.getBoundingClientRect().top]);
      }
      waiting.sort((a, b) => a[1] - b[1]);
      tracker.current.onInView(waiting.map(([id]) => id), now);
    }

    // Hand the free download slots to the cards that have waited out the delay.
    for (const id of tracker.current.dwelled(now)) {
      if (inFlight.current.size >= maxParallel) break;
      if (granted.current.has(id)) continue;
      const card = cards.current.get(id);
      if (!card) continue;
      granted.current.add(id);
      inFlight.current.add(id);
      card.onGrant();
    }

    const wait = tracker.current.millisUntilNext(now);
    if (wait >= 0) dwellTimer.current = setTimeout(evaluate, wait);
  }, [maxParallel]);

  /** A granted card's request has settled (loaded, empty or failed): next in line. */
  const release = useCallback((id) => {
    if (!inFlight.current.delete(id)) return;
    evaluate();
  }, [evaluate]);

  /**
   * The observer is built on first use, not in an effect: a card's own effect
   * runs before this provider's (React runs children's effects first), so by
   * the time the first card registers the observer has to already exist.
   */
  const ensureObserver = useCallback(() => {
    if (observer.current || !supported()) return observer.current;
    const fraction = visibleFractionRef.current;
    observer.current = new IntersectionObserver((entries) => {
      let changed = false;
      for (const entry of entries) {
        for (const [, card] of cards.current) {
          if (card.node !== entry.target) continue;
          const visible = enoughVisible(entry, fraction);
          if (card.visible !== visible) { card.visible = visible; changed = true; }
        }
      }
      if (changed) evaluate();
    }, { threshold: [0, 0.25, fraction, 0.75, 1] });
    return observer.current;
  }, [evaluate]);

  const register = useCallback((id, node, onGrant) => {
    const io = ensureObserver();
    if (!io) return () => {};
    cards.current.set(id, { node, onGrant, visible: false });
    io.observe(node);
    evaluate();
    return () => {
      if (observer.current) observer.current.unobserve(node);
      cards.current.delete(id);
      // A card scrolled off the list releases its slot; a download already
      // started is still allowed to finish and lands in the query cache.
      inFlight.current.delete(id);
      evaluate();
    };
  }, [evaluate, ensureObserver]);

  useEffect(() => {
    if (!supported()) return undefined;
    ensureObserver();

    const onMove = () => {
      moving.current = true;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        idleTimer.current = null;
        moving.current = false;
        evaluate();
      }, idleMs);
      evaluate();
    };
    // Capture phase: a scroll event does not bubble, but it does reach a
    // capturing listener on window from whichever element actually scrolled.
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('wheel', onMove, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('resize', evaluate);
    document.addEventListener('visibilitychange', evaluate);

    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('wheel', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('resize', evaluate);
      document.removeEventListener('visibilitychange', evaluate);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (dwellTimer.current) clearTimeout(dwellTimer.current);
      if (observer.current) observer.current.disconnect();
      observer.current = null;
    };
  }, [evaluate, ensureObserver, idleMs]);

  const value = useMemo(() => ({
    register,
    release,
    isGranted: (id) => granted.current.has(id),
  }), [register, release]);

  return <RouteGateContext.Provider value={value}>{children}</RouteGateContext.Provider>;
}

/**
 * One card's slot in the gate.
 *
 * @param activityId the card's activity
 * @param wanted     false for cards that need no route at all (no GPS, or the
 *                   route is already cached) — those never register
 * @returns `[ref, allowed, release]`: put `ref` on the map area, load the route
 *          once `allowed`, and call `release` when the request settles.
 */
export function useRouteSlot(activityId, wanted) {
  const gate = useContext(RouteGateContext);
  const ref = useRef(null);
  const key = String(activityId);
  // No gate or no IntersectionObserver: load as the feed did before (immediately).
  const ungated = !gate || !supported();
  const [allowed, setAllowed] = useState(() => (ungated ? !!wanted : (gate ? gate.isGranted(key) : false)));

  // `allowed` is deliberately not a dependency: the grant must not tear the
  // registration down, or the card would hand its download slot back before
  // the request had even started.
  useEffect(() => {
    if (ungated) { setAllowed(!!wanted); return undefined; }
    if (!wanted || !ref.current) return undefined;
    // Granted earlier this session (a remount after filtering): straight through.
    if (gate.isGranted(key)) { setAllowed(true); return undefined; }
    return gate.register(key, ref.current, () => setAllowed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate, key, wanted, ungated]);

  const release = useCallback(() => {
    if (gate && !ungated) gate.release(key);
  }, [gate, key, ungated]);

  return [ref, allowed, release];
}

export default RouteGateProvider;
